/**
 * M.2 — NIPE tool: proposeSharePointDocument
 * B.5 — Mark NIPE-authored drafts with `nipe_ai_` prefix +
 *       `source: nipe` frontmatter before queuing.
 *
 * **Write proposal** — does NOT write the file directly. Enqueues a
 * pending-write row + caches the body in `pendingWriteBodies.ts`. The
 * user must approve via the NipePendingWritesTray before any Graph PUT
 * fires.
 *
 * @see {@link ../../writes/lanes.md} — this tool dispatches to lane C
 * (NipePendingWrites). On approval, lane C calls into lane B
 * (VaultSaveProvider) for the actual PUT.
 *
 * Body capped at 80 KB. Longer drafts must be split client-side. The
 * 80 KB check happens BEFORE the B.5 marker injection (frontmatter
 * adds ~25 bytes; rejection still based on the model-supplied body).
 */

import { markNipeAuthored } from '../markNipeAuthored';
import type { NipeTool } from './types';

export interface ProposeSharePointDocumentContext {
  /**
   * Optional. Some sessions have no SharePoint write integration
   * configured (e.g. demo desktop with no portal connected); in that
   * case the handler surfaces a friendly error string instead of
   * throwing.
   */
  proposeSharePointDocument?: (input: {
    folderPath: string;
    fileName: string;
    body: string;
    summary: string;
  }) => Promise<{ pendingWriteId: string; status: 'queued' }>;
}

export const proposeSharePointDocumentTool: NipeTool<ProposeSharePointDocumentContext> =
  {
    schema: {
      type: 'function',
      function: {
        name: 'proposeSharePointDocument',
        description:
          "Propose a NEW markdown document to be saved into the firm's SharePoint. This does NOT write the file. It queues the proposal in the pending-writes tray; the user reviews and approves before the write fires. Returns the pending-write id and a 'queued' status — acknowledge to the user that the proposal is awaiting approval.",
        parameters: {
          type: 'object',
          properties: {
            folderPath: {
              type: 'string',
              description:
                "Drive-relative folder path the doc should land in (e.g. 'Matters/Hadley/Drafts'). Use 'root' for the drive root.",
            },
            fileName: {
              type: 'string',
              description:
                "File name including extension (e.g. 'demand-letter.md'). The model is responsible for the .md / .txt suffix.",
            },
            body: {
              type: 'string',
              description:
                'Full document body (markdown). Limit ~80kb; longer drafts should be split.',
            },
            summary: {
              type: 'string',
              description:
                'One-line description shown in the tray row (e.g. "Demand letter to opposing counsel re: Hadley deadline").',
            },
          },
          required: ['folderPath', 'fileName', 'body', 'summary'],
        },
      },
    },
    mutatesRemote: true,
    requiresApproval: true,
    handler: async (args, ctx) => {
      if (!ctx.proposeSharePointDocument) {
        return {
          error: 'SharePoint write integration not available in this session',
        };
      }
      const folderPath =
        typeof args.folderPath === 'string' ? args.folderPath : '';
      const fileName = typeof args.fileName === 'string' ? args.fileName : '';
      const body = typeof args.body === 'string' ? args.body : '';
      const summary = typeof args.summary === 'string' ? args.summary : '';
      if (!folderPath) return { error: 'folderPath required' };
      if (!fileName) return { error: 'fileName required' };
      if (!body) return { error: 'body required' };
      if (!summary) return { error: 'summary required' };
      if (body.length > 80_000) {
        return { error: 'body exceeds 80kb limit; split the draft' };
      }
      // B.5 — Apply NIPE-authored markers (filename prefix + frontmatter
      // source: nipe) before queuing so the vault badge + audit log can
      // distinguish NIPE drafts from human-authored writes.
      const marked = markNipeAuthored({ fileName, body });
      const result = await ctx.proposeSharePointDocument({
        folderPath,
        fileName: marked.fileName,
        body: marked.body,
        summary,
      });
      return {
        pendingWriteId: result.pendingWriteId,
        status: result.status,
        note: 'The proposed document is queued in the pending-writes tray. Tell the user to approve it from the tray to save the file. NIPE cannot write without approval.',
      };
    },
  };
